import { GetServerSideProps } from 'next'
import ErrorPage from 'next/error'
import ReactMarkdown from 'react-markdown'
import Image from '../../components/Image'
import Layout from '../../components/layout'
import PageWidth from '../../components/PageWidth'
import { StrapiGlobal } from '../../types/StrapiGlobal'
import { getStrapiMedia, handleRedirection } from '../../utils'
import { getLocalizedParams } from '../../utils/localize'

interface UniversalsProps {
  global: StrapiGlobal
  category: {
    attributes: {
      title: string
      body: string
    }
  }
  posts: {
    id: number
    attributes: {
      title: string
      body: string
    }
  }[]
  preview: boolean
}

const renderers = {
  img: ({ alt, src }: { alt: string; src: string }) => (
    <Image
      className="helpPage__postImage"
      image={{
        data: {
          attributes: {
            url: src,
            alternativeText: alt,
            width: 300,
            height: 300,
            formats: {},
          },
        },
      }}
      objectFit="contain"
    ></Image>
  ),
}

const HelpCategoryPage = ({
  global,
  category,
  posts,
  preview,
}: UniversalsProps) => {
  if (posts === null) {
    return <ErrorPage statusCode={404} />
  }

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Layout global={global} preview={preview}>
      <PageWidth className="helpPage">
        <h1 className="helpPage__title">{category?.attributes?.title}</h1>
        <p className="helpPage__body">{category?.attributes?.body}</p>
        <div className="helpPage__links">
          {posts.map((post) => (
            <a
              className="helpPage__link"
              key={post.id}
              href={`#post_${post.id}`}
            >
              {post.attributes.title}
            </a>
          ))}
        </div>
        <div className="helpPage__posts">
          {posts.map((post) => (
            <article
              className="helpPage__post"
              key={post.id}
              id={`post_${post.id}`}
            >
              <p className="helpPage__postTitle">{post.attributes.title}</p>
              <ReactMarkdown
                className="post__body markdown"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                transformImageUri={(src) => {
                  if (src.startsWith('/cms')) {
                    return src
                  }
                  return getStrapiMedia(src)
                }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                components={renderers}
              >
                {post.attributes.body}
              </ReactMarkdown>
            </article>
          ))}
        </div>
      </PageWidth>
    </Layout>
  )
}

// This gets called on every request
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug } = getLocalizedParams(context.query)

  if (slug.length === 0) {
    return handleRedirection(context.preview, 'home')
  }

  try {
    const [category, posts] = await Promise.all([
      fetch(
        `${process.env.NEXT_INTERNAL_API_URL}api/help-categories?filters[slug][$eq]=${slug}&populate=deep,15`
      )
        .then((res) => res.json())
        .then((res) => res.data[0]),
      fetch(
        `${process.env.NEXT_INTERNAL_API_URL}api/helps?filters[help_category][slug][$eq]=${slug}&populate=deep,15`
      )
        .then((res) => res.json())
        .then((res) => res.data),
    ])

    return {
      props: { category, posts, preview: context.preview || null },
    }
  } catch (error) {
    return {
      props: { pageData: null },
    }
  }
}

export default HelpCategoryPage
